import { useCallback, useEffect, useRef, useState } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { useIntl } from 'dibs-react-intl';
import { localStorage } from 'dibs-browser-storage';
import { useDebouncedCallback } from 'dibs-react-hooks/exports/useDebouncedCallback.js';
import {
    getMeasurementUnit,
    getSupportedMeasurementUnit,
    type MeasurementUnit,
} from 'dibs-intl/exports/units';

import { SharedEditMeasurementUnitPreference as EditMeasurementUnitPreferenceMutation } from '../../mutations/SharedEditMeasurementUnitPreference.js';
import { LOCAL_STORAGE_KEY } from '../../constants/measurementUnits.ts';

const PREFERENCE_COMMIT_DELAY_MS = 300;

export const usePdpDimensionUnit = ({
    userMeasurementUnit,
    userServiceId,
}: {
    userMeasurementUnit?: string | null;
    userServiceId?: string | null;
}): [MeasurementUnit, (unit: MeasurementUnit) => void] => {
    const intl = useIntl();
    const environment = useRelayEnvironment();
    const localeUnit = getMeasurementUnit(intl.locale);
    const hasHydratedPreference = useRef(false);
    const [measurementUnit, setMeasurementUnit] = useState<MeasurementUnit>(() =>
        getSupportedMeasurementUnit(userMeasurementUnit || localeUnit)
    );

    // Don't re-sync from the server after each click — out-of-order mutation responses flash the toggle.
    useEffect(() => {
        if (hasHydratedPreference.current) {
            return;
        }

        if (userMeasurementUnit) {
            hasHydratedPreference.current = true;
            setMeasurementUnit(getSupportedMeasurementUnit(userMeasurementUnit));
            return;
        }

        const storedUnit = localStorage.getItem(LOCAL_STORAGE_KEY) as string;
        if (storedUnit) {
            setMeasurementUnit(getSupportedMeasurementUnit(storedUnit));
        }
    }, [userMeasurementUnit]);

    const commitMeasurementUnitPreference = useCallback(
        (unit: MeasurementUnit): void => {
            if (!userServiceId) {
                return;
            }

            EditMeasurementUnitPreferenceMutation.commit(environment, {
                userServiceId,
                measurementUnit: unit,
                onCompleted: () => {},
                onError: () => {},
            });
        },
        [environment, userServiceId]
    );

    const [debouncedCommitPreference, , callPendingCommit] = useDebouncedCallback(
        commitMeasurementUnitPreference,
        PREFERENCE_COMMIT_DELAY_MS
    );

    useEffect(() => {
        return () => {
            callPendingCommit();
        };
    }, [callPendingCommit]);

    const onChange = useCallback(
        (unit: MeasurementUnit): void => {
            setMeasurementUnit(unit);
            localStorage.setItem(LOCAL_STORAGE_KEY, unit);
            debouncedCommitPreference(unit);
        },
        [debouncedCommitPreference]
    );

    return [measurementUnit, onChange];
};
